import { matchPath } from 'react-router';

import { type PageTitlesTranslation } from '@/assets/locales/pageTitles/pageTitles.translation.en.ts';
import { type RouteEnum, type RouteModel } from '@/layouts/routes/Route.model.ts';

export interface BreadcrumbItem {
  label: keyof PageTitlesTranslation;
  /** Concrete (param-substituted) path for navigation; never contains `:params`. */
  path: string;
  /** Original route template (enum value), used to look up the `RouteModel`. */
  routePath: RouteEnum;
}

/**
 * Substitutes `:param` segments in a route template so breadcrumb parents
 * link to real URLs (e.g. `/companies/:id` → `/companies/42`). Exact param
 * names win; remaining placeholders fall back to unmatched values from the
 * current match in declaration order (handles renames like `companyId` → `:id`).
 */
const fillPathParams = (
  template: string,
  params: Readonly<Record<string, string | undefined>>,
): string => {
  const values = Object.values(params).filter((value): value is string => value != null);
  const used = new Set<string>();

  return template.replace(/:([A-Za-z0-9_]+)/g, (_match, name: string) => {
    const exact = params[name];
    if (exact != null) {
      used.add(exact);
      return exact;
    }
    const next = values.find(value => !used.has(value));
    if (next != null) {
      used.add(next);
      return next;
    }
    return `:${name}`;
  });
};

/**
 * Resolves the breadcrumb trail for a pathname from route declarations
 * (OCD-305): matches the current route, then walks the optional `parent`
 * chain. Pure — no router or i18n access. Paths with params match via
 * `matchPath` (e.g. `/companies/:id` ↔ `/companies/42`); each item's `path`
 * is filled with concrete values so desktop parents are clickable.
 */
export const buildBreadcrumbTrail = (
  routes: RouteModel[],
  pathname: string,
  locationState?: unknown,
): BreadcrumbItem[] => {
  const current = routes.find(route => matchPath(route.path, pathname) !== null);
  if (!current?.breadcrumb) {
    return [];
  }
  const currentMatch = matchPath(current.path, pathname);
  const params = (currentMatch?.params ?? {}) as Record<string, string | undefined>;

  const trail: BreadcrumbItem[] = [
    {
      label: current.breadcrumb.label,
      path: fillPathParams(current.path, params),
      routePath: current.path,
    },
  ];

  let parentId = current.breadcrumb.dynamicParent?.(locationState) ?? current.breadcrumb.parent;
  const visited = new Set<RouteEnum>([current.path]);
  while (parentId && !visited.has(parentId)) {
    visited.add(parentId);
    const parent = routes.find(route => route.path === parentId);
    if (!parent?.breadcrumb) {
      break;
    }
    trail.unshift({
      label: parent.breadcrumb.label,
      path: fillPathParams(parent.path, params),
      routePath: parent.path,
    });
    parentId = parent.breadcrumb.parent;
  }

  return trail;
};
